/**
 * Ghost Member Authentication & Cross-Suite Teleportation Bridge.
 * Synchronizes sessions across Map, Monitor, Capital, and Studio.
 */

import { ghostStudioAuthHandoffUrl } from '../config/auth';

const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export const GHOST_ACTIVE_TOKEN_KEY = "aixenergy_ghost_active_token";
export const GHOST_TOKEN_HOLD_KEY = "aixenergy_ghost_token_hold";
export const GHOST_TOKEN_HOLD_GEN_KEY = "aixenergy_ghost_token_hold_gen";
export const GHOST_STUDIO_SESSION_KEY = "aixenergy_studio_session_token";

const PAGE_HOLD_GENERATION =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `hold-${Math.random().toString(36).slice(2)}`;

function readHeldToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const generation = window.sessionStorage.getItem(GHOST_TOKEN_HOLD_GEN_KEY);
    if (generation !== PAGE_HOLD_GENERATION) return null;
    return window.sessionStorage.getItem(GHOST_TOKEN_HOLD_KEY);
  } catch {
    return null;
  }
}

function holdToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(GHOST_TOKEN_HOLD_KEY, token);
    window.sessionStorage.setItem(GHOST_TOKEN_HOLD_GEN_KEY, PAGE_HOLD_GENERATION);
  } catch {}
}

function clearHeldToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(GHOST_TOKEN_HOLD_KEY);
    window.sessionStorage.removeItem(GHOST_TOKEN_HOLD_GEN_KEY);
  } catch {}
}

export function getActiveGhostToken(): string | null {
  if (typeof window === "undefined") return null;

  // 1. Direct from sessionStorage
  try {
    const direct = window.sessionStorage.getItem(GHOST_ACTIVE_TOKEN_KEY);
    if (direct && direct.trim()) return direct.trim();
  } catch {}

  // 2. Direct from localStorage
  try {
    const localToken =
      window.localStorage.getItem(GHOST_ACTIVE_TOKEN_KEY) ||
      window.localStorage.getItem("ghost_member_token") ||
      window.localStorage.getItem("aixenergy_member_jwt");
    if (localToken && localToken.trim()) return localToken.trim();
  } catch {}

  // 3. Document cookies
  try {
    const match = document.cookie.match(/(?:^|;\s*)(?:aixenergy_ghost_jwt|ghost-members-ssr)=([^;]+)/);
    if (match && match[1]) {
      const cookieToken = decodeURIComponent(match[1].trim());
      if (cookieToken) return cookieToken;
    }
  } catch {}

  return readHeldToken();
}

export function setActiveGhostToken(token: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (token && token.trim()) {
      const cleanToken = token.trim();
      window.sessionStorage.setItem(GHOST_ACTIVE_TOKEN_KEY, cleanToken);
      window.localStorage.setItem(GHOST_ACTIVE_TOKEN_KEY, cleanToken);
      window.localStorage.setItem("ghost_member_token", cleanToken);
      holdToken(cleanToken);
    } else {
      window.sessionStorage.removeItem(GHOST_ACTIVE_TOKEN_KEY);
      window.localStorage.removeItem(GHOST_ACTIVE_TOKEN_KEY);
      window.localStorage.removeItem("ghost_member_token");
      window.localStorage.removeItem("aixenergy_member_jwt");
      clearHeldToken();
    }
  } catch {}
}

function decodeTokenValue(value: string): string | null {
  try {
    return decodeURIComponent(value.trim());
  } catch {
    return value.trim();
  }
}

export function peekGhostTokenFromUrl(): string | null {
  if (typeof window === "undefined") return null;

  const url = new URL(window.location.href);

  // Check query params: ?ghost_token=... or ?token=... or ?jwt=...
  const queryToken =
    url.searchParams.get("ghost_token") ||
    url.searchParams.get("token") ||
    url.searchParams.get("jwt");
  if (queryToken) {
    return decodeTokenValue(queryToken);
  }

  // Check hash: #ghost_token=... or #token=...
  const hash = window.location.hash.replace(/^#/, "");
  if (hash) {
    const hashParams = hash.startsWith("ghost_token=") || hash.startsWith("token=")
      ? new URLSearchParams(hash)
      : new URLSearchParams(hash.includes("?") ? hash.split("?").pop() ?? "" : hash);

    const hashToken = hashParams.get("ghost_token") || hashParams.get("token") || hashParams.get("jwt");
    if (hashToken) {
      return decodeTokenValue(hashToken);
    }

    const directMatch = window.location.hash.match(/^#(?:ghost_token|token|jwt)=([^&]+)$/);
    if (directMatch?.[1]) {
      return decodeTokenValue(directMatch[1]);
    }
  }

  return null;
}

export function readGhostTokenFromUrl(): string | null {
  const liveToken = peekGhostTokenFromUrl();
  if (liveToken) {
    holdToken(liveToken);
    setActiveGhostToken(liveToken);
    return liveToken;
  }
  return readHeldToken();
}

export function clearGhostTokenFromUrl(): void {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  let changed = false;

  if (url.searchParams.has("ghost_token")) {
    url.searchParams.delete("ghost_token");
    changed = true;
  }
  if (url.searchParams.has("token")) {
    url.searchParams.delete("token");
    changed = true;
  }
  if (url.searchParams.has("jwt")) {
    url.searchParams.delete("jwt");
    changed = true;
  }

  if (url.hash.includes("ghost_token=") || url.hash.includes("token=") || url.hash.includes("jwt=")) {
    url.hash = "";
    changed = true;
  }

  if (url.pathname.includes("/api/auth/callback") || url.pathname.includes("/auth/callback")) {
    const next = url.searchParams.get("next") || "/";
    url.searchParams.delete("next");
    const targetUrl = `${window.location.origin}${next}${url.search}${url.hash}`;
    window.history.replaceState({}, "", targetUrl);
    clearHeldToken();
    return;
  }

  if (changed) {
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, "", nextUrl || "/");
  }

  clearHeldToken();
}

/**
 * Builds cross-suite teleportation URL that appends the active Ghost token.
 */
export function buildSuiteCrossUrl(baseUrl: string, queryParams?: Record<string, string>): string {
  if (typeof window === "undefined") return baseUrl;
  try {
    const url = new URL(baseUrl, window.location.href);
    if (queryParams) {
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          url.searchParams.set(k, v);
        }
      });
    }

    const token = getActiveGhostToken();
    if (token) {
      url.hash = `ghost_token=${encodeURIComponent(token)}`;
    }
    return url.toString();
  } catch {
    return baseUrl;
  }
}

export function parseGhostSessionBody(body: string): string | null {
  if (!body) return null;
  const trimmed = body.trim();
  if (!trimmed) return null;

  if (JWT_PATTERN.test(trimmed)) {
    return trimmed;
  }

  try {
    const payload = JSON.parse(trimmed) as { identity?: string; token?: string };
    return payload.identity?.trim() || payload.token?.trim() || null;
  } catch {
    return null;
  }
}

export interface GhostAuthMessage {
  type: "aixenergy-ghost-token";
  token: string;
  member?: {
    uuid?: string | null;
    email?: string | null;
    name?: string | null;
  } | null;
}

export function isGhostAuthMessage(data: unknown): data is GhostAuthMessage {
  if (!data || typeof data !== "object") return false;
  const message = data as { type?: unknown; token?: unknown };
  return message.type === "aixenergy-ghost-token" && typeof message.token === "string";
}

export function ghostHandoffProfile(message: GhostAuthMessage): {
  name: string | null;
  email: string | null;
} {
  const member = message.member;
  if (!member || typeof member !== "object") {
    return { name: null, email: null };
  }
  const name = typeof member.name === "string" ? member.name.trim() : "";
  const email = typeof member.email === "string" ? member.email.trim() : "";
  return {
    name: name || null,
    email: email || null,
  };
}

export function getGhostSignInUrl(): string {
  const currentUrl = typeof window !== "undefined" ? window.location.href : "https://studio.aixenergy.io/";
  const currentOrigin = typeof window !== "undefined" ? window.location.origin : "https://studio.aixenergy.io";
  return ghostStudioAuthHandoffUrl(currentUrl, { parentOrigin: currentOrigin });
}

export function clearGhostSession(): void {
  setActiveGhostToken(null);
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.clear();
      window.localStorage.removeItem(GHOST_ACTIVE_TOKEN_KEY);
      window.localStorage.removeItem("ghost_member_token");
      window.localStorage.removeItem("aixenergy_member_jwt");
      window.localStorage.setItem("aixenergy_studio_signed_out", "true");
    } catch {}
  }
}
